<script setup lang="ts">
// apps/web/src/components/common/AppFooter.vue
//
// Global footer with legal + support + subscription management links.
// Customer portal URL fetched from /api/config (not hardcoded).
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useLicense } from '@/composables/useLicense';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const { isPro } = useLicense();

const customerPortalUrl = ref('');

onMounted(async () => {
  try {
    const res = await fetch('/api/config');
    if (res.ok) {
      const data = await res.json();
      customerPortalUrl.value = data.customerPortalUrl || '';
    }
  } catch {
    // Network error — portal URL stays empty, fallback to email
  }
});

function goPrivacy() {
  router.push('/about/privacy');
}
function goRefund() {
  router.push('/refund-policy');
}
function goTerms() {
  router.push('/terms');
}
function goActivate() {
  router.push('/activate');
}
function goTrends() {
  router.push('/trends');
}
function goFeedback() {
  router.push('/feedback');
}
function manageSubscription() {
  if (customerPortalUrl.value) {
    window.open(customerPortalUrl.value, '_blank', 'noopener');
  } else {
    router.push('/feedback');
  }
}
</script>

<template>
  <footer class="app-footer">
    <div class="footer-inner">
      <div class="brand-section">
        <span
          class="brand"
          @click="router.push('/')"
        >{{ t('common.brand') }}</span>
        <p class="copyright">
          © 2026 DumpDeck · {{ t('footer.builtBy') }}
          <a href="/#/feedback">Feedback</a>
        </p>
      </div>
      <nav class="links">
        <button
          class="link"
          @click="goTerms"
        >
          {{ t('footer.terms') }}
        </button>
        <button
          class="link"
          @click="goRefund"
        >
          {{ t('footer.refundPolicy') }}
        </button>
        <button
          class="link"
          @click="goPrivacy"
        >
          {{ t('footer.privacy') }}
        </button>
        <button
          class="link"
          @click="goFeedback"
        >
          {{ t('footer.support') }}
        </button>
        <button
          v-if="isPro"
          class="link"
          @click="goTrends"
        >
          Trends
        </button>
        <button
          v-if="isPro"
          class="link"
          @click="manageSubscription"
        >
          {{ t('footer.manageSubscription') }}
        </button>
        <button
          v-else
          class="link"
          @click="goActivate"
        >
          {{ t('footer.activate') }}
        </button>
      </nav>
      <p class="copyright">
        © 2026 DumpDeck · {{ t('footer.builtBy') }}
        <a href="/#/feedback">Feedback</a>
      </p>
    </div>
  </footer>
</template>

<style scoped>
.app-footer {
  margin-top: var(--space-6);
  padding: var(--space-5) var(--space-4) var(--space-4);
  border-top: 1px solid var(--border-subtle);
  background: var(--bg-canvas);
}
.footer-inner {
  max-width: 1200px; margin: 0 auto;
  display: flex; flex-direction: column; gap: var(--space-3);
}
.brand-section {
  display: flex; align-items: baseline; gap: var(--space-3);
  flex-wrap: wrap;
}
.brand {
  font-family: var(--font-sans); font-weight: 700; font-size: 16px;
  cursor: pointer;
  background: linear-gradient(90deg, var(--accent-mint), var(--accent-blue));
  -webkit-background-clip: text; background-clip: text; color: transparent;
}
.tagline {
  font-size: 12px; color: var(--text-muted);
}
.links {
  display: flex; gap: var(--space-4); flex-wrap: wrap;
}
.link {
  background: transparent; border: none; cursor: pointer;
  color: var(--text-secondary); font-size: 12px;
  padding: 0; font-family: var(--font-sans);
}
.link:hover { color: var(--accent-mint); }
.copyright {
  font-size: 11px; color: var(--text-muted);
  margin: 0;
}
.copyright a {
  color: var(--text-secondary);
  text-decoration: none;
}
.copyright a:hover { color: var(--accent-mint); }
</style>
